import { IonLabel, IonList, IonListHeader } from '@ionic/react';
import { Form } from 'formik';
import { isEmpty } from 'lodash';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { ActionButton } from '../../../../../../components/button/action-button';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { CloseButton } from '../../../../../../components/button/close-button';
import { IntercomHelp } from '../../../../../../components/help/intercom-help';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { trackMultiEdit } from '../../../../../../config/analytics';
import { IntercomArticle } from '../../../../../../config/intercom';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useIsMounted } from '../../../../../../util/hooks';
import { logger } from '../../../../../../util/logger';
import { FlowItemFormComponent } from '../../../../shared/component/flow-item/edit/flow-item-form';
import { ParameterListExtensions } from '../../../../shared/component/flow-item-parameter/parameter-list';
import {
  WidgetToggler,
  WidgetTogglerProps,
} from '../../../../shared/component/widget/widget-toggler';
import {
  WidgetExtensionProps,
  WidgetExtensions,
} from '../../../../shared/component/widget/widget.types';
import { useFlowPermissionsContext } from '../../../../shared/hook/flow-permissions.hooks';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItem,
  FlowItemParameter,
  FlowSelectedItemInfo,
  SystemUnit,
} from '../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { WeldProcessSettingsForm } from './weld-process-settings-form';
import { WeldingFlowItemEditActionBar } from './welding-flow-item-edit-action-bar';
import './welding-flow-item-edit.css';

interface FlowItemEditProps {
  onClosed?: () => void;
  items: FlowItem[];
  onUpdateItem: (item: FlowItem, updateFlowConfig?: boolean) => Promise<void>;
  onDeleteItems: (indices: number[]) => void;
  system: SystemUnit;
  selectedItemInfo?: FlowSelectedItemInfo;
  readOnly?: boolean;
}

const WeldingFlowItemMultiEdit = (props: FlowItemEditProps): ReactElement => {
  const [saving, setSaving] = useState<boolean>(false);
  const {
    onUpdateItem,
    system,
    items,
    selectedItemInfo,
    onClosed,
    onDeleteItems,
    readOnly,
  } = props;
  const { flowItemService, flow } =
    useFlowServices<BeaconWeldingData.FlowConfig>();
  const [modifiedRefs, setModifiedRefs] = useState<string[]>([]);
  const updateFlowConfigOnSave = useRef<boolean>(false);

  const hasFieldsSelected = modifiedRefs.length > 0;

  const hasWeldsSelected = useMemo(() => {
    return items.findIndex(
      (i) => i.ref === BeaconWeldingData.FlowItemRef.WELD
    ) > -1
      ? true
      : false;
  }, [items]);

  const defaultWeldItem = useMemo((): FlowItem => {
    const parameters =
      flowItemService.getApplicationDefaultParameterSet()?.data.parameters ||
      [];
    const item = { parameters, ref: BeaconWeldingData.FlowItemRef.WELD };

    return item;
  }, [flowItemService]);

  const weldForceIncludeTags: BeaconWeldingData.FlowItemParameterTag[] =
    useMemo(() => {
      const tags: BeaconWeldingData.FlowItemParameterTag[] = [];

      if ((selectedItemInfo?.linearStitch?.count ?? 0) > 0) {
        tags.push(BeaconWeldingData.FlowItemParameterTag.STITCH);
      }

      if ((selectedItemInfo?.tack?.count ?? 0) > 0) {
        tags.push(BeaconWeldingData.FlowItemParameterTag.TACK);
      }

      return tags;
    }, [selectedItemInfo]);

  const isMounted = useIsMounted();

  const onSubmit = async (item: FlowItem) => {
    setSaving(true);

    const params = modifiedRefs.reduce((prev, ref) => {
      const path = ref.split('.');
      const params = item.parameters.find((params) => {
        return params.ref === path[1];
      });

      if (params) {
        prev.push(params);
      }
      return prev;
    }, [] as FlowItemParameter[]);
    const itemToSave = { ...item, parameters: params };

    logger.info('saving multi-edit changes');
    await onUpdateItem(itemToSave, updateFlowConfigOnSave.current);
    trackMultiEdit(updateFlowConfigOnSave.current);

    if (isMounted()) {
      setSaving(false);
    }
  };

  const widgetExtensions = useMemo((): WidgetExtensions => {
    const onFieldToggle = (enabled: boolean, fieldname: string) => {
      if (isMounted()) {
        if (enabled) {
          if (!modifiedRefs.includes(fieldname)) {
            setModifiedRefs([...modifiedRefs, fieldname]);
          }
        } else {
          const remaining = modifiedRefs.filter((r) => r !== fieldname);
          setModifiedRefs(remaining);
        }
      }
    };

    const props = {
      onToggle: onFieldToggle,
    } as WidgetTogglerProps & WidgetExtensionProps;

    return {
      rightElement: <WidgetToggler {...props} />,
    };
  }, [isMounted, modifiedRefs]);

  const itemHeaderTitle = getLocalizedText('welding:multi_edit_items_header', {
    numOfItems: items.length,
  });

  const { parameterPermissions } = useFlowPermissionsContext();

  const extensions: ParameterListExtensions = useMemo(() => {
    return {
      widgetExtensions,
      allowedParametersMap: parameterPermissions,
    };
  }, [widgetExtensions, parameterPermissions]);

  return (
    <FlowItemFormComponent
      onSubmit={onSubmit}
      item={defaultWeldItem}
      hasFlowChanges={hasFieldsSelected && items.length > 0}
    >
      {({ errors, setValidationSchema, submitForm }) => {
        const isReadyToSubmit =
          hasFieldsSelected && items.length > 0 && isEmpty(errors);

        return (
          <div className={'flow-panel-container'}>
            <IonListHeader className={'panel-header'}>
              <IonLabel>{itemHeaderTitle}</IonLabel>
              <IntercomHelp
                articleId={IntercomArticle.MULTI_EDIT_WELD_SETTINGS}
              />
            </IonListHeader>
            <div className="header-divider" />
            <Form>
              <WeldingFlowItemEditActionBar
                items={items}
                readOnly={readOnly}
                onDelete={async (itemsForDelete) => {
                  const indices: number[] = itemsForDelete.map(
                    (itemForDelete) => {
                      return flow.items.findIndex(
                        (i) => i.id === itemForDelete.id
                      );
                    }
                  );
                  onDeleteItems(indices);
                }}
              />
              {hasWeldsSelected && (
                <WeldProcessSettingsForm
                  system={system}
                  extensions={extensions}
                  parameterOptions={{
                    ...BeaconWeldingData.DefaultWeldParameterSetParameterFormOptions,
                    forceIncludeTags: [
                      ...(BeaconWeldingData
                        .DefaultWeldParameterSetParameterFormOptions
                        .forceIncludeTags ?? []),
                      ...weldForceIncludeTags,
                    ],
                    forceExcludeRefs: [
                      BeaconWeldingData.FlowItemParameterRef.QNEAR,
                      BeaconWeldingData.FlowItemParameterRef.WAYPOINT,
                    ],
                  }}
                  setValidationSchema={setValidationSchema}
                  // always default readOnly to true since multi-edit leverages the widget toggler
                  readOnly={true}
                />
              )}
              <RightAlignedButtons>
                <CloseButton
                  onClose={() => {
                    if (typeof onClosed === 'function') {
                      onClosed();
                    }
                  }}
                />
                <ActionButton
                  label={getLocalizedText('welding:save_options')}
                  buttonProps={{
                    color: isReadyToSubmit ? 'primary' : 'medium',
                    disabled: !isReadyToSubmit || saving,
                    fill: isReadyToSubmit ? 'solid' : 'outline',
                  }}
                >
                  {({ close }) => {
                    return (
                      <IonList lines="full">
                        <IonItem
                          onClick={(e) => {
                            e.preventDefault();
                            updateFlowConfigOnSave.current = false;
                            submitForm();
                            close();
                          }}
                          href="#"
                          detail={false}
                        >
                          {getLocalizedText('welding:update_these_only')}
                        </IonItem>
                        <IonItem
                          lines="none"
                          disabled={readOnly}
                          onClick={(e) => {
                            e.preventDefault();
                            updateFlowConfigOnSave.current = true;
                            submitForm();
                            close();
                          }}
                          href="#"
                          detail={false}
                        >
                          {getLocalizedText(
                            'welding:update_these_and_part_defaults'
                          )}
                        </IonItem>
                      </IonList>
                    );
                  }}
                </ActionButton>
              </RightAlignedButtons>
            </Form>
          </div>
        );
      }}
    </FlowItemFormComponent>
  );
};

export { WeldingFlowItemMultiEdit };
